import { Stack, useTheme } from "@mui/material";
import { FoodsIcon, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { AuditLogEntry } from "src/api/client-api/auditLog/auditLogSchemas";
import {
    auditLogRecipient,
    formatAuditLogValue,
    isIsoDateTime,
    normalizeAuditLogChanges,
} from "./auditLogPresentation";
import { useAuditLogLabels } from "./useAuditLogLabels";

type AuditLogChangesProps = {
    entry: AuditLogEntry;
    isStacked: boolean;
};

const RECIPIENT_FIELD = "identity";

const isActorReference = (value: unknown, actorId: string | null): boolean =>
    actorId !== null && typeof value === "object" && value !== null && "id" in value && value.id === actorId;

export const AuditLogChanges = (props: AuditLogChangesProps) => {
    const { entry, isStacked } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const labels = useAuditLogLabels();
    const changes = normalizeAuditLogChanges(entry.changes);
    const recipient = auditLogRecipient(entry);

    if (changes.length === 0) {
        return (
            <Typography variant="small-regular" color="text.secondary">
                {t("settings.audit_log.changes.empty", "No value recorded for this entry.")}
            </Typography>
        );
    }

    const fallbackFor = (field: string, value: unknown): string | null => {
        if (field === RECIPIENT_FIELD) {
            return recipient;
        }

        return isActorReference(value, entry.actorId) ? entry.actorLabel : null;
    };

    const render = (field: string, value: unknown): string => {
        const formatted = formatAuditLogValue(value, fallbackFor(field, value));

        return typeof value === "string" && isIsoDateTime(value) ? labels.utcTimestamp(value) : formatted;
    };

    const hasDiff = changes.some((change) => change.isDiff);

    return (
        <Stack gap={theme.spacing(1)}>
            {isStacked || hasDiff === false ?
                <Stack gap={theme.spacing(1)}>
                    {changes.map((change) => (
                        <Stack
                            key={change.field}
                            gap={theme.spacing(0.5)}
                            sx={{
                                border: `1px solid ${theme.palette.grey[200]}`,
                                borderRadius: theme.spacing(1),
                                padding: theme.spacing(1.5),
                            }}
                        >
                            <Typography variant="small-semibold">{labels.changedField(change.field)}</Typography>
                            {change.isDiff && (
                                <>
                                    <Typography variant="small-regular" color="text.secondary">
                                        {t("settings.audit_log.changes.before", "Before")}
                                    </Typography>
                                    <ValueBox>{render(change.field, change.before)}</ValueBox>
                                    <Stack alignItems="center">
                                        <FoodsIcon icon="arrowDownShort" size={2} />
                                    </Stack>
                                    <Typography variant="small-regular" color="text.secondary">
                                        {t("settings.audit_log.changes.after", "After")}
                                    </Typography>
                                </>
                            )}
                            <ValueBox>{render(change.field, change.after)}</ValueBox>
                        </Stack>
                    ))}
                </Stack>
            :   <Stack sx={{ border: `1px solid ${theme.palette.grey[200]}`, borderRadius: theme.spacing(0.5) }}>
                    <ChangeRow
                        field={t("settings.audit_log.changes.field", "Field")}
                        before={t("settings.audit_log.changes.before", "Before")}
                        after={t("settings.audit_log.changes.after", "After")}
                        isHeader
                    />
                    {changes.map((change) => (
                        <ChangeRow
                            key={change.field}
                            field={labels.changedField(change.field)}
                            before={render(change.field, change.before)}
                            after={render(change.field, change.after)}
                        />
                    ))}
                </Stack>
            }

            <Typography variant="small-regular" color="text.secondary">
                {t(
                    "settings.audit_log.changes.masking",
                    "Sensitive values (secrets, passwords) are masked both before AND after the change: the log keeps no cleartext trace of them.",
                )}
            </Typography>
        </Stack>
    );
};

type ValueBoxProps = {
    children: string;
};

const ValueBox = (props: ValueBoxProps) => {
    const { children } = props;
    const theme = useTheme();

    return (
        <Stack
            sx={{
                backgroundColor: theme.palette.grey[50],
                borderRadius: theme.spacing(0.5),
                paddingX: theme.spacing(1),
                paddingY: theme.spacing(0.5),
            }}
        >
            <Typography variant="small-regular" sx={{ wordBreak: "break-word" }}>
                {children}
            </Typography>
        </Stack>
    );
};

type ChangeRowProps = {
    field: string;
    before: string;
    after: string;
    isHeader?: boolean;
};

const ChangeRow = (props: ChangeRowProps) => {
    const { field, before, after, isHeader = false } = props;
    const theme = useTheme();

    const cell = (content: string, isFirst: boolean) => (
        <Typography
            variant={isHeader ? "small-regular" : "small-medium"}
            color={isHeader ? "text.secondary" : "text.primary"}
            sx={{ flex: isFirst ? "0 0 30%" : 1, wordBreak: "break-word" }}
        >
            {content}
        </Typography>
    );

    return (
        <Stack
            direction="row"
            gap={theme.spacing(1)}
            paddingX={theme.spacing(1)}
            paddingY={theme.spacing(0.5)}
            sx={{
                borderBottom: `1px solid ${theme.palette.grey[200]}`,
                "&:last-of-type": { borderBottom: "none" },
                backgroundColor: isHeader ? theme.palette.grey[50] : "transparent",
            }}
        >
            {cell(field, true)}
            {cell(before, false)}
            {cell(after, false)}
        </Stack>
    );
};
